// @ts-nocheck
import BigNumber from 'bignumber.js';

import {
  getFootfallAttributionStats,
  getPaginatedReportingStats,
  getReportingStatsUserId
} from '@/modules/reporting/reporting.resources';

import type { PaginatedStatsResult } from 'sonata-static-storage/lib/common/common.types';

export const DWELL_TIME_KEYS = [
  'dwellTimeAcc_1+',
  'dwellTimeAcc_5+',
  'dwellTimeAcc_10+',
  'dwellTimeAcc_15+',
  'dwellTimeAcc_20+',
  'dwellTimeAcc_25+',
  'dwellTime_1-5',
  'dwellTime_5-10',
  'dwellTime_10-15',
  'dwellTime_15-20',
  'dwellTime_20-25',
  'dwellTime_25+'
];

function getFormattedStats(selectedChart: string, stats?: Record<string, any>[]) {
  const formatNumberDecimals = (number: BigNumber) => number.decimalPlaces(2).toNumber();
  return (stats || []).map(statsEntry => ({
    ...statsEntry,
    ...(selectedChart === 'bidMultiplier' && {
      bidMultiplier: formatNumberDecimals(new BigNumber(statsEntry.impressions).dividedBy(statsEntry.playouts))
    }),
    ...(selectedChart === 'visitBounceRate' && {
      visit_bounce_rate: formatNumberDecimals(
        new BigNumber(100).minus(new BigNumber(statsEntry.visit).dividedBy(statsEntry.clicks).multipliedBy(100))
      ),
      visit_retention_rate: formatNumberDecimals(
        new BigNumber(statsEntry.visit).dividedBy(statsEntry.clicks).multipliedBy(100)
      )
    })
  }));
}

async function getProgressStatsFromBackups(
  userId: string,
  campaignIds: string[],
  requestBody: Record<string, any>,
  requestToken: Record<string, any>
): Promise<Record<string, any>[]> {
  const { groups, ...restParams } = requestBody.params;
  return getReportingStatsUserId(
    {
      ...requestBody,
      params: {
        ...restParams,
        timeStep: groups[0]
      }
    },
    requestToken,
    userId,
    campaignIds.join()
  ).then(stats =>
    stats.map(({ sdate, ...restStats }: { restStats: Record<string, any>; sdate: string }) => ({
      ...restStats,
      [groups[0]]: sdate
    }))
  );
}

export function getProgressStatsRequestBody(
  currencyOptions: {
    baseCurrency: string;
    currency: string;
  },
  dateInterval: {
    end: string;
    start: string;
    timezone: string;
  },
  groupBy: string,
  outputMetrics: string[],
  cartographyFilter?: Record<string, string[]>,
  variableFilter?: Record<string, string[]>,
  useBackups = false
): { opts: Record<string, any>; params: Record<string, any> } {
  return {
    opts: {
      currencyOptions
    },
    params: {
      ...(cartographyFilter && { cartography: cartographyFilter }),
      dateInterval,
      filters: {
        ...variableFilter
      },
      ...(useBackups && {
        groups: [groupBy]
      }),
      ...(!useBackups && {
        groupOptions: [
          {
            field: groupBy,
            others: false,
            size: 1000
          }
        ]
      }),
      outputMetrics
    }
  };
}

export async function fetchProgressStats(
  userId: string,
  campaignIds: string[],
  chartTimeStep: string,
  selectedChart: string,
  requestBody: Record<string, any>,
  useBackups = false,
  requestToken: Record<string, any>
): Promise<(Record<string, any> | Record<string, any>[])[]> {
  const statsPromises = [];
  if (useBackups) {
    statsPromises.push(getProgressStatsFromBackups(userId, campaignIds, requestBody, requestToken));
  } else {
    statsPromises.push(
      getPaginatedReportingStats(userId, campaignIds.join(','), requestBody, undefined, requestToken).then(
        (result: PaginatedStatsResult) =>
          selectedChart === 'dwellTimeChart' ? [result.metadata.totalResults] : result.results
      )
    );
    if (selectedChart === 'storeVisits') {
      statsPromises.push(
        getFootfallAttributionStats(
          userId,
          campaignIds,
          {
            dateInterval: requestBody.params.dateInterval,
            filters: requestBody.params.filters,
            timeStep: chartTimeStep
          },
          requestToken
        )
      );
    }
  }
  return Promise.all(statsPromises).then(([mainStats, secondaryStats]) => [
    selectedChart === 'dwellTimeChart' ? mainStats : getFormattedStats(selectedChart, mainStats || []),
    secondaryStats
  ]);
}
